import type { CropRegion } from "../api";

/**
 * The crop frame's arithmetic (ADR-0048), kept apart from the pointer
 * handling so it can be tested without a browser.
 *
 * Everything is in the picture's own pixels, as the browser displays it
 * (upright): `aspect` is width over height in those pixels, so a square frame
 * is square in the picture and not merely on a screen that scaled it.
 */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Corner = "nw" | "ne" | "sw" | "se";

/** The shortest side a crop may have; the server refuses less. */
export const MIN_CROP_SIDE = 64;

/** parseAspect reads "w:h", or null for a free frame. */
export function parseAspect(shape: string | null): number | null {
  if (!shape) return null;
  const [w, h] = shape.split(":").map(Number);
  return w && h ? w / h : null;
}

/** The largest frame of the shape, centred — or the whole picture when free. */
export function initialBox(width: number, height: number, aspect: number | null): Box {
  if (aspect === null) return { x: 0, y: 0, w: width, h: height };
  let w = width;
  let h = w / aspect;
  if (h > height) {
    h = height;
    w = h * aspect;
  }
  return { x: (width - w) / 2, y: (height - h) / 2, w, h };
}

/** The frame dragged by (dx, dy), kept inside the picture. */
export function moveBox(box: Box, dx: number, dy: number, width: number, height: number): Box {
  return {
    ...box,
    x: clamp(box.x + dx, 0, width - box.w),
    y: clamp(box.y + dy, 0, height - box.h),
  };
}

/**
 * The frame with one corner dragged by (dx, dy). The opposite corner stays
 * where it is, the frame stays inside the picture and no smaller than the
 * server allows, and a locked frame keeps its shape, following whichever of
 * the two drags moved it further.
 */
export function resizeBox(
  box: Box,
  corner: Corner,
  dx: number,
  dy: number,
  width: number,
  height: number,
  aspect: number | null,
): Box {
  const west = corner === "nw" || corner === "sw";
  const north = corner === "nw" || corner === "ne";
  // The corner that does not move.
  const ax = west ? box.x + box.w : box.x;
  const ay = north ? box.y + box.h : box.y;
  // Room from the fixed corner to the picture's edge, in the drag's direction.
  const roomW = west ? ax : width - ax;
  const roomH = north ? ay : height - ay;
  // A picture smaller than the minimum is cropped no smaller than itself.
  const minSide = Math.min(MIN_CROP_SIDE, width, height);

  let w = box.w + (west ? -dx : dx);
  let h = box.h + (north ? -dy : dy);
  if (aspect === null) {
    w = clamp(w, Math.min(minSide, roomW), roomW);
    h = clamp(h, Math.min(minSide, roomH), roomH);
  } else {
    // Whichever drag moved the frame further leads, growing or shrinking.
    if (Math.abs(h * aspect - box.w) > Math.abs(w - box.w)) w = h * aspect;
    const maxW = Math.min(roomW, roomH * aspect);
    const minW = Math.min(Math.max(minSide, minSide * aspect), maxW);
    w = clamp(w, minW, maxW);
    h = w / aspect;
  }
  return { x: west ? ax - w : ax, y: north ? ay - h : ay, w, h };
}

/** The frame as fractions of the picture, which is what the server takes. */
export function toRegion(box: Box, width: number, height: number): CropRegion {
  const x = clamp(box.x / width, 0, 1);
  const y = clamp(box.y / height, 0, 1);
  return {
    x,
    y,
    width: clamp(box.w / width, 0, 1 - x),
    height: clamp(box.h / height, 0, 1 - y),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}
